import React, { Component } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { sendTextMessage, sendAttachment, sendUserTyping } from '../../reducers/history/actions';
import { updateChatInput } from '../../reducers/ui/actions';
import withConfig from '../../context/withConfig';
import withIsMobile from '../../context/withIsMobile';
import { updateScroll } from '../MessageHistory';
import ScrollUpIcon from '../icons/ScrollUpIcon';

import ExpandingTextArea from './ExpandingTextArea';
import ChatUploadIcon from '../icons/ChatUploadIcon';
import { visibleActivities } from '../../reducers/history/selectors';

// 10MB file upload limit
const FILE_SIZE_LIMIT = 1024 * 1024 * 10;

const ChatInputWrapper = withIsMobile(styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 16px;
  box-sizing: border-box;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
`);

const InputContainer = styled.div`
  flex-grow: 1;
  background-color: ${props => (props.disabled ? '#808080' : props.theme.botMessageBubbleColor)};
  border-radius: 24px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const ScrollButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.userMessageBubbleColor};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledScrollButton = styled(ScrollButton)`
  color: ${props => props.theme.userMessageFontColor};

  &:disabled {
    background: #808080;
    cursor: not-allowed;

    &:hover {
      opacity: 1;
    }
  }
`;

// Define keyframes for the button animations
const scrollToCenter = keyframes`
  from {
    transform: translateX(0) rotate(0deg);
    right: 16px;
  }
  to {
    transform: translateX(calc(-50% + 46px)) rotate(180deg);
    right: 50%;
  }
`;

const scrollToCorner = keyframes`
  from {
    transform: translateX(calc(-50% + 46px)) rotate(180deg);
    right: 50%;
  }
  to {
    transform: translateX(0) rotate(0deg);
    right: 16px;
  }
`;

const FakeScrollButton = styled(StyledScrollButton)`
  display: ${props => (props.visible ? 'block' : 'none')};
  position: absolute;
  bottom: 16px;
  right: ${props => (props.centered ? '50%' : '16px')};
  transform: ${props =>
    props.centered ? 'translateX(calc(-50% + 46px)) rotate(180deg)' : 'translateX(0) rotate(0deg)'};
  transition: all 0.5s ease-in-out;
  animation: ${props => {
    if (!props.animating) return 'none';
    return props.centered
      ? css`
          ${scrollToCenter} 0.5s ease-in-out forwards;
        `
      : css`
          ${scrollToCorner} 0.5s ease-in-out forwards;
        `;
  }};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-right: 5px;
`;

const checkIsInOperatorConversation = activities => {
  if (!activities || !activities.length) return false;
  const activitiesWithOperatorType = activities.filter(
    item => item.type === 'operatorHandBack' || item.type === 'operatorHandOver'
  );
  if (activitiesWithOperatorType && activitiesWithOperatorType.length) {
    return (
      activitiesWithOperatorType[activitiesWithOperatorType.length - 1].type === 'operatorHandOver'
    );
  }
  const chatItem = activities.filter(item => item.inOperatorConversation !== undefined);
  // const chatItem = activities.findLast(item => 'inOperatorConversation' in item);
  if (!chatItem || !chatItem.length) return false;
  return chatItem[chatItem.length - 1].inOperatorConversation;
};

class ChatInput extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    skipTranslation: PropTypes.bool,
    fieldDisabled: PropTypes.bool,
    onButtonCenteredChange: PropTypes.func,
    updateChatInput: PropTypes.func.isRequired,
    sendUserTyping: PropTypes.func.isRequired,
    chatInputValue: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      objectUrl: null,
      buttonCentered: false,
      buttonAnimating: false,
      showScrollToBottom: false,
      showInputs: true
    };
  }

  componentDidMount() {
    // Add scroll event listener to detect when user scrolls up
    const messageScroller = document.querySelector('#transcriptView').parentElement;
    if (messageScroller) {
      messageScroller.addEventListener('scroll', this.handleMessageScroll);
    }

    // Notify parent component of initial buttonCentered state
    if (this.props.onButtonCenteredChange) {
      this.props.onButtonCenteredChange(this.state.buttonCentered);
    }
  }

  componentWillUnmount() {
    URL.revokeObjectURL(this.state.objectUrl);

    // Remove scroll event listener
    const messageScroller = document.querySelector('#transcriptView').parentElement;
    if (messageScroller) {
      messageScroller.removeEventListener('scroll', this.handleMessageScroll);
    }
  }

  handleMessageScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const distance = Math.abs(scrollTop + clientHeight - scrollHeight);

    // Add a buffer value to prevent jumping when padding changes
    // When buttonCentered is true, buffer is 0 (no padding)
    // When buttonCentered is false, buffer is 80 (has padding)
    const buffer = this.state.buttonCentered ? 0 : 80;
    const isNearBottom = distance - buffer < 100;

    // Show center button when scrolled up and button not yet shown
    if (!isNearBottom && !this.state.showScrollToBottom) {
      this.scrollToCenter();
    } else if (isNearBottom && this.state.showScrollToBottom) {
      // Hide center button when near bottom and button is currently shown
      this.scrollToCorner();
    }
  };

  handleSend = () => {
    const {
      suggestedActions,
      chatInputValue,
      disabled,
      sendTextMessage,
      inputFieldRef,
      skipTranslation
    } = this.props;

    const checkSuggestedActions = suggestedActions && suggestedActions.actions;
    const checkchoiceNodeOriginText =
      checkSuggestedActions &&
      checkSuggestedActions.filter(suggestion => suggestion.title === chatInputValue);
    const choiceNodeOriginText =
      checkchoiceNodeOriginText && checkchoiceNodeOriginText.length
        ? checkchoiceNodeOriginText[0].choiceNodeOriginText
        : undefined;

    if (chatInputValue && !disabled) {
      sendTextMessage({ text: chatInputValue, choiceNodeOriginText, skipTranslation });
    }
    inputFieldRef.current.focus();
  };

  handleUpload = () => {
    const file = this.fileInputRef.current.files[0];
    const fileUrl = URL.createObjectURL(file);
    this.setState({ objectUrl: fileUrl });

    if (!file) {
      return;
    }

    if (!['image', 'pdf'].some(item => file.type.toLowerCase().includes(item))) {
      alert('Only allow for image or pdf');
      return;
    }

    // Limit file size to 10MB
    if (file.size > FILE_SIZE_LIMIT) {
      alert('File too large, please try again');
      return;
    }

    this.fileInputRef.current.value = null;

    this.props.sendAttachment(file, fileUrl);
    this.props.inputFieldRef.current.focus();
  };

  handleInputChange = e => {
    this.props.updateChatInput({ value: e.target.value });
  };

  handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.handleSend();
    } else {
      this.props.sendUserTyping();
    }
  };

  scrollToLatest = () => {
    this.scrollToCorner(); // First trigger the padding change

    // Wait for padding transition to complete before scrolling
    setTimeout(() => {
      // Force scroll to bottom using the updateScroll function from MessageHistory
      updateScroll(true);

      // As a backup, also try to scroll using the DOM directly
      const messageScroller = document.querySelector('#transcriptView').parentElement;
      if (messageScroller) {
        messageScroller.scrollTop = messageScroller.scrollHeight;
      }
    }, 300); // Match the padding-bottom transition duration
  };

  scrollToCenter = () => {
    if (!this.state.buttonCentered) {
      this.setState(
        {
          showScrollToBottom: true,
          buttonCentered: true,
          buttonAnimating: true,
          showInputs: false
        },
        () => {
          // Notify parent component after state update
          if (this.props.onButtonCenteredChange) {
            this.props.onButtonCenteredChange(true);
          }
        }
      );

      setTimeout(() => {
        this.setState({ buttonAnimating: false });
      }, 500);
    }
  };

  scrollToCorner = () => {
    if (this.state.buttonCentered) {
      this.setState(
        {
          buttonCentered: false,
          buttonAnimating: true,
          showInputs: true
        },
        () => {
          setTimeout(() => {
            this.setState({ showScrollToBottom: false });
          }, 500);

          // Notify parent component after state update
          if (this.props.onButtonCenteredChange) {
            this.props.onButtonCenteredChange(false);
          }
        }
      );

      setTimeout(() => {
        this.setState({ buttonAnimating: false });
      }, 500);
    }
  };

  render() {
    const {
      chatInputValue,
      chatPanelVisible,
      inOperatorConversation,
      fieldDisabled,
      config
    } = this.props;
    const { buttonCentered, buttonAnimating, showScrollToBottom, showInputs } = this.state;

    return (
      <ChatInputWrapper data-test-id="chat-input">
        <InputContainer isVisible={showInputs} disabled={fieldDisabled}>
          <ExpandingTextArea
            value={chatInputValue}
            onChange={this.handleInputChange}
            onKeyDown={this.handleInputKeyDown}
            placeholder={config.strings.chatPlaceholder}
            autoFocus={chatPanelVisible}
            disabled={fieldDisabled}
          />
        </InputContainer>
        <ButtonsContainer isVisible={showInputs}>
          {inOperatorConversation && (
            <React.Fragment>
              <input
                id="ambit-shell-upload-input"
                type="file"
                style={{ display: 'none' }}
                ref={this.fileInputRef}
                onChange={this.handleUpload}
                disabled={fieldDisabled}
              />
              <Label htmlFor="ambit-shell-upload-input">
                <ChatUploadIcon />
              </Label>
            </React.Fragment>
          )}
          <StyledScrollButton onClick={this.handleSend} title="Submit" disabled={fieldDisabled}>
            <ScrollUpIcon />
          </StyledScrollButton>
        </ButtonsContainer>

        <FakeScrollButton
          onClick={this.scrollToLatest}
          title={buttonCentered ? 'Return to corner' : 'Move to center'}
          centered={buttonCentered}
          animating={buttonAnimating}
          visible={showScrollToBottom}
        >
          <ScrollUpIcon />
        </FakeScrollButton>
      </ChatInputWrapper>
    );
  }
}

export default connect(
  state => {
    const activities = visibleActivities(state);
    const lastActivity = activities && activities.length ? activities[activities.length - 1] : null;

    return {
      suggestedActions: lastActivity && lastActivity.suggestedActions,
      chatInputValue: state.ui.chatInput,
      chatPanelVisible: state.ui.chatPanelVisible,
      inputFieldRef: state.ui.inputFieldRef,
      inOperatorConversation: checkIsInOperatorConversation(activities)
    };
  },
  {
    sendTextMessage,
    updateChatInput,
    sendAttachment,
    sendUserTyping
  }
)(withConfig(ChatInput));



// WEBPACK FOOTER //
// ./src/components/ChatInput/index.js